import React, {
    useRef,
    useCallback,
    cloneElement,
    Children,
    MouseEvent,
    useState,
    useEffect,
    createContext,
    forwardRef,
    useImperativeHandle,
} from 'react';
import FocusTrap from 'focus-trap-react';
import { createPortal } from 'react-dom';
import { usePopper } from 'react-popper';
import useKey from 'react-use/lib/useKey';
import useToggle from 'react-use/lib/useToggle';
import { Box } from '../../layout/Box';
import { Dropdown } from './styles';
import { MenuContextValue, MenuProps, MenuRef } from './types';

export const MenuContext = createContext<MenuContextValue | null>(null);

export const Menu = forwardRef<MenuRef, MenuProps>(
    (
        {
            trigger,
            onTriggerClick,
            options = { placement: 'bottom-end' },
            children,
            root,
            zIndex,
            returnFocusOnDeactivate = true,
            ...other
        },
        ref,
    ) => {
        const [open, toggleOpen] = useToggle(false);
        const containerRef = useRef<HTMLDivElement | null>(null);
        const [referenceElement, setReferenceElement] = useState<HTMLElement | null>(null);
        const [popperElement, setPopperElement] = useState<HTMLDivElement | null>(null);
        const { styles, attributes, update } = usePopper(referenceElement, popperElement, options);
        const [shouldClose, setShouldClose] = useState(false);

        const menuItemsCount = Children.count(children);

        const onTriggerClickCallback = useCallback(
            (event: MouseEvent<any>) => {
                event.preventDefault();
                event.stopPropagation();
                toggleOpen();
                update?.();
                onTriggerClick?.();
            },
            // eslint-disable-next-line react-hooks/exhaustive-deps
            [update, toggleOpen],
        );

        const onClose = useCallback(() => toggleOpen(false), [toggleOpen]);

        useImperativeHandle(
            ref,
            () => ({
                close: onClose,
            }),
            [onClose],
        );

        useKey('Escape', onClose, {}, [onClose]);

        useEffect(() => {
            if (shouldClose) {
                onClose();
                setShouldClose(false);
            }
        }, [shouldClose, onClose]);

        useEffect(() => {
            if (!referenceElement || !popperElement) {
                return;
            }

            const onClickAway = (e: Event) => {
                if (referenceElement === e.target || referenceElement.contains(e.target as any)) {
                    setShouldClose(true);

                    return;
                }

                if (
                    !e.target ||
                    popperElement === e.target ||
                    referenceElement === e.target ||
                    popperElement.contains(e.target as any) ||
                    referenceElement.contains(e.target as any)
                ) {
                    return;
                }

                onClose();
                e.stopPropagation();
            };

            const clickOutRoot = root || window;
            clickOutRoot.addEventListener('touchstart', onClickAway, { capture: true });
            clickOutRoot.addEventListener('mousedown', onClickAway, { capture: true });

            return () => {
                clickOutRoot.removeEventListener('touchstart', onClickAway, { capture: true });
                clickOutRoot.removeEventListener('mousedown', onClickAway, { capture: true });
            };
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [referenceElement, popperElement, onClose]);

        if (menuItemsCount === 0) {
            return null;
        }

        return (
            <Box ref={containerRef} {...other}>
                {cloneElement(trigger, {
                    ref: setReferenceElement,
                    onClick: onTriggerClickCallback,
                })}
                {open
                    ? createPortal(
                          <FocusTrap
                              focusTrapOptions={{
                                  returnFocusOnDeactivate,
                              }}
                          >
                              <Box
                                  ref={setPopperElement}
                                  zIndex={zIndex || 1000}
                                  style={styles.popper}
                                  {...attributes.popper}
                              >
                                  <Box
                                      style={styles.offset}
                                      display={open ? 'block' : 'none'}
                                      pt={1}
                                  >
                                      <MenuContext.Provider value={{ onClose }}>
                                          <Dropdown>{children}</Dropdown>
                                      </MenuContext.Provider>
                                  </Box>
                              </Box>
                          </FocusTrap>,
                          root || document.body,
                      )
                    : null}
            </Box>
        );
    },
);

Menu.displayName = 'Menu';
